import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-234" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-235" data-mp="3dqpcv9l1uave">
        <div className="text-center mb-10" data-id="element-236" data-mp="3dpnwcpl9kw6a">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-neutral-900 uppercase" data-id="element-237" data-mp="38qulz619xlac">
            See Inside Heavy Equipment Instructor <span className="text-amber-600" data-id="element-238" data-mp="2l2beuttyneju">Atlas</span>
          </h2>
          <p className="text-xl font-semibold text-neutral-900 max-w-3xl mx-auto" data-id="element-239" data-mp="2h17x21scr8fz">
            Professional Heavy Machinery Training Material You Can Actually Teach From
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" data-id="element-240" data-mp="2pztnsk7z9tru">
          <div className="relative bg-neutral-100 border border-neutral-200 rounded-2xl shadow-sm shadow-neutral-900/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-241" data-mp="2pz1p30j33fxc">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-242" data-mp="18ssnfwvslbj">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}`} data-id="element-243" data-mp="66ulgsk3zz07">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-244" data-mp="2s7fd3ighw9d1" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-neutral-900/90 text-white flex items-center justify-center hover:bg-neutral-900 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="View image larger" data-id="element-245" data-mp="2egk7qg57gae7">
              <Maximize2 size={18} data-id="element-246" data-mp="1vihgrvt3k3dr" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-yellow-400 text-neutral-900 border-2 border-white shadow-lg flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2" aria-label="Previous image" data-id="element-247" data-mp="3uszcq0dtqf32">
            <ChevronLeft size={24} data-id="element-248" data-mp="2ih3xfixko5oq" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-yellow-400 text-neutral-900 border-2 border-white shadow-lg flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2" aria-label="Next image" data-id="element-249" data-mp="23tsid726m3jv">
            <ChevronRight size={24} data-id="element-250" data-mp="1puat6qkz4o9z" />
          </button>
        </div>

        <div className="max-w-3xl mx-auto text-center mt-6 min-h-[4.5rem]" aria-live="polite" data-id="element-251" data-mp="1pvlwyqef5w13">
          <p className="text-base font-bold text-neutral-900 uppercase tracking-wide" data-id="element-252" data-mp="169sljq5uz70o">
            {slide.title}
          </p>
          <p className="text-sm text-neutral-800/80 mt-1" data-id="element-253" data-mp="1g6oo7f92syxb">{slide.caption}</p>
        </div>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-254" data-mp="1b7c06r6gxlt5">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${dotIndex === index ? 'w-8 bg-amber-500' : 'w-2.5 bg-neutral-900/25 hover:bg-neutral-900/40'}`} aria-label={`Go to image ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-255" data-mp="1p5s64fwib8a8" data-mpi={dotIndex} />)}
        </div>

        <div className="text-center mt-10 max-w-3xl mx-auto" data-id="element-256" data-mp="2az67dlhl59jt">
          <p className="text-lg font-bold text-neutral-900" data-id="element-257" data-mp="2awtjkrp5c4yu">
            <span className="text-amber-600" data-id="element-258" data-mp="2fz2dbq9ufb20">REAL TRAINING MATERIAL. REAL TECHNICAL DIAGRAMS.</span>{' '}
            ONE COMPLETE VISUAL SYSTEM.
          </p>
          <p className="text-lg text-neutral-800/80 mt-3" data-id="element-259" data-mp="2pnu3ptz99nat">
            Inside the system, you&apos;ll find equipment anatomy, mechanical illustrations, component
            comparisons, industrial worksite scenarios, hazard-recognition exercises and professional
            instructor resources.
          </p>
          <p className="text-lg font-semibold text-neutral-900 mt-3" data-id="element-260" data-mp="33nv4l9fisuqa">
            Everything organized to support more structured and visually engaging training sessions.
          </p>
        </div>
      </div>

      <AnimatePresence data-id="element-261" data-mp="19a3zmn84yz16">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-neutral-950/90 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.alt} onClick={() => setIsZoomed(false)} data-id="element-262" data-mp="19bf2mktrc2r8">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300" aria-label="Close enlarged image" data-id="element-263" data-mp="3dsbgxstsjtpc">
              <X size={22} data-id="element-264" data-mp="buakvmyw7fn" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-265" data-mp="2q1xy8sx3j77f" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}